import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, Output, Input, Inject, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

const UI_OVERFLOW_MENU_DIVIDER = 'divider';
/**
 * A component that displays an overflow menu.
 */
class E11OverflowComponent {
    constructor(elementRef, document, cdr) {
        this.elementRef = elementRef;
        this.document = document;
        this.cdr = cdr;
        /**
         * The ID of the menu control.
         * @type {string}
         * @default random string
         * @example 'menu_control_123456'
         */
        this.idMenuControl = `menu_control_${Math.ceil(Math.random() * 1000000).toString()}`;
        /**
         * The ID of the options menu.
         * @type {string}
         * @default random string
         * @example 'options_123456'
         */
        this.idOptionsMenu = `options_${Math.ceil(Math.random() * 1000000).toString()}`;
        /**
         * The class of the option menu.
         * @type {string}
         * @default ''
         */
        this.optionMenuClass = '';
        /**
         * The class of the icon.
         * @type {string}
         * @default ''
         */
        this.iconClass = '';
        /**
         * The class of the container.
         * @type {string}
         * @default ''
         */
        this.containerClass = '';
        /**
         * Whether the menu should show in content.
         * @type {boolean}
         * @default false
         */
        this.showInContent = false;
        /**
         * The icon to display.
         * @type {string}
         * @default 'more_vert'
         */
        this.icon = 'more_vert';
        /**
         * The color of the icon.
         * @type {OverflowMenuColor}
         * @default 'neutral'
         */
        this.iconColor = 'neutral';
        /**
         * The options to display in the menu.
         * @type {Option[]}
         * @default []
         */
        this.options = [];
        /**
         * The position of the menu.
         * @type {ElementPosition | undefined}
         * @default 'bottom left'
         */
        this.menuPosition = 'bottom left';
        /**
         * Whether the menu should close on select.
         * @type {boolean}
         * @default true
         */
        this.closeOnSelect = true;
        /**
         * Whether the selected icon should show.
         * @type {boolean}
         * @default true
         */
        this.showSelectedIcon = true;
        /**
         * Whether the menu should open on click.
         * @type {boolean}
         * @default true
         */
        this.openOnClick = true;
        /**
         * Whether the menu should close on mouse out.
         * @type {boolean}
         * @default false
         */
        this.closeOnMouseOut = false;
        /**
         * Whether the menu should close on escape.
         * @type {boolean}
         * @default true
         */
        this.closeOnEscape = true;
        /**
         * Whether the menu should close on click outside.
         * @type {boolean}
         * @default false
         */
        this.closeOnClickOutside = false; // keep off
        /**
         * The value of the selected option.
         * @deprecated This event is deprecated. Please use {@link onClickOptionEvent} instead.
         * @type {Option}
         * @default undefined
         */
        this.clickOptionEvent = new EventEmitter();
        /**
         * The value of the selected option.
         * @type {Option}
         * @default undefined
         */
        this.onClickOptionEvent = new EventEmitter();
        this.menuActive = false;
        this.posLeft = false;
        this.UI_OVERFLOW_MENU_DIVIDER = UI_OVERFLOW_MENU_DIVIDER;
        this.propagateChange = () => null;
        this.menuPosLeft = '';
        this.isBrowser = inject(IS_BROWSER);
    }
    ngAfterViewInit() {
        if (!this.isBrowser)
            return null;
        if (this.openOnClick) {
            // Lazy Listener to avoid too much detect changes cycles
            this.createLazylistener();
        }
        this.menuControl = document.getElementById(this.idMenuControl);
        this.menuControlRect = document.getElementById(this.idMenuControl).getBoundingClientRect();
        this.optionsContainer = document.getElementById(this.idOptionsMenu);
        this.optionsContainerRect = document.getElementById(this.idOptionsMenu).getBoundingClientRect();
        if (this.menuPosition === 'bottom left' && this.menuMaxWidth) {
            this.menuPosLeft = '-' + (this.menuMaxWidth - this.menuControlRect.width) + 'px';
        }
        this.setMenuStyle({
            top: this.menuControlRect.height,
            left: this.menuPosLeft,
            width: this.menuWidth,
            maxWidth: this.menuMaxWidth,
        });
    }
    /**
     * Sets the style of the menu.
     * @param {Object} style - The style object.
     * @param {number} style.top - The top position of the menu.
     * @param {string} style.left - The left position of the menu.
     * @param {number} [style.width] - The width of the menu.
     * @param {number} style.maxWidth - The maximum width of the menu.
     * @returns {void}
     * @private
     */
    setMenuStyle(style) {
        if (!this.optionsContainer)
            return;
        this.optionsContainer.style.top = `${style.top}px`;
        this.optionsContainer.style.left = style.left;
        this.optionsContainer.style.width = `${style.maxWidth}px`;
    }
    /**
     * Creates a lazy listener to avoid too many detect changes cycles.
     * @returns {void}
     */
    createLazylistener() {
        if (!this.isBrowser)
            return null;
        this.addListener = () => {
            const listenerFn = (event) => {
                if (!this.elementRef.nativeElement.contains(event.target)) {
                    this.closeMenu();
                    this.cdr.detectChanges();
                }
            };
            this.document.addEventListener('click', listenerFn);
            this.removeLIstener = () => this.document.removeEventListener('click', listenerFn);
        };
    }
    /**
     * Handles the click event on an option.
     * @param {Option} option - The selected option.
     * @param {Event} event - The click event.
     * @returns {void}
     */
    clickOption(option, event) {
        event.stopPropagation();
        this.value = option;
        // TODO: remove this in the next major release
        this.clickOptionEvent.emit(this.value);
        this.onClickOptionEvent.emit(this.value);
        if (this.closeOnSelect) {
            this.closeMenu();
        }
    }
    /**
     * Opens the menu.
     * @returns {void}
     */
    openMenu() {
        this.menuActive = true;
        if (this.openOnClick && this.addListener) {
            this.addListener();
        }
    }
    /**
     * Closes the menu.
     * @returns {void}
     */
    closeMenu() {
        this.menuActive = false;
        if (this.openOnClick && this.removeLIstener) {
            this.removeLIstener();
        }
    }
    /**
     * Closes the menu.
     * Specifically for close directives - @Todo: refactor
     * @returns {void}
     */
    close() {
        this.closeMenu();
    }
    /**
     * Toggles the menu.
     * @param {Event} event - The click event.
     * @returns {void}
     */
    toggleMenu(event) {
        event.stopPropagation();
        if (this.openOnClick) {
            this.menuActive ? this.closeMenu() : this.openMenu();
        }
    }
    /**
     * Returns the name of the option.
     * @param {Option} option - The option.
     * @returns {string} The name of the option.
     */
    optionName(option) {
        if (typeof option === 'string') {
            return option === this.UI_OVERFLOW_MENU_DIVIDER ? '' : option;
        }
        else {
            return option.name || option.value;
        }
    }
    /**
     * Handles the mouse out event.
     * @returns {void}
     */
    mouseOut() {
        if (this.closeOnMouseOut) {
            this.closeMenu();
        }
    }
    /**
     * Returns whether the option is active.
     * @param {Option} option - The option.
     * @returns {boolean} Whether the option is active.
     */
    isOptionActive(option) {
        if (!this.value) {
            return false;
        }
        if (typeof option === 'string' && typeof this.value === 'string') {
            return option === this.UI_OVERFLOW_MENU_DIVIDER ? false : option === this.value;
        }
        else if (typeof this.value !== 'string' && typeof option !== 'string') {
            return option.value === this.value.value;
        }
        else {
            return false;
        }
    }
    /**
     * Opens the menu on mouse over.
     * @returns {void}
     */
    onMouseover() {
        if (!this.openOnClick) {
            this.openMenu();
        }
    }
    /**
     * Closes the menu on mouse out.
     * @returns {void}
     */
    onMouseout() {
        if (!this.openOnClick) {
            this.closeMenu();
        }
    }
    /**
     * Writes the value.
     * @param {Option} obj - The value.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    writeValue(obj) {
        this.value = obj;
    }
    /**
     * Registers the change.
     * @param {Function} fn - The function.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    /**
     * Registers the touch.
     * @param {Function} fn - The function.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    registerOnTouched(_fn) { }
    /**
     * Sets the disabled state.
     * @param {boolean} _isDisabled - Whether the component is disabled.
     * @returns {void}
     * @implements {ControlValueAccessor}
     * @see {@link ControlValueAccessor}
     */
    setDisabledState(_isDisabled) { }
    ngOnDestroy() {
        if (this.removeLIstener) {
            this.removeLIstener();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowComponent, deps: [{ token: i0.ElementRef }, { token: DOCUMENT }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11OverflowComponent, isStandalone: true, selector: "e11-overflow", inputs: { idMenuControl: "idMenuControl", idOptionsMenu: "idOptionsMenu", optionMenuClass: "optionMenuClass", iconClass: "iconClass", containerClass: "containerClass", showInContent: "showInContent", icon: "icon", iconColor: "iconColor", options: "options", menuWidth: "menuWidth", menuMinWidth: "menuMinWidth", menuMaxWidth: "menuMaxWidth", menuPosition: "menuPosition", closeOnSelect: "closeOnSelect", showSelectedIcon: "showSelectedIcon", openOnClick: "openOnClick", closeOnMouseOut: "closeOnMouseOut", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside" }, outputs: { clickOptionEvent: "clickOptionEvent", onClickOptionEvent: "onClickOptionEvent" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: E11OverflowComponent,
                multi: true,
            },
        ], ngImport: i0, template: `
    <div class="e11-overflow-menu e11-relative {{ containerClass }}" (mouseleave)="mouseOut()">
      <span
        id="{{ idMenuControl }}"
        class="material-icons-outlined e11-cursor-pointer e11-rounded-full e11-p-1 e11-overflow-menu-{{ iconColor }} {{ iconClass }}"
        (click)="toggleMenu($event)"
        [ngClass]="{ active: menuActive }"
      >
        {{ icon }}
      </span>
      <div
        id="{{ idOptionsMenu }}"
        class="e11-overflow-menu-options e11-border e11-border-skin-app-borders e11-bg-white  {{ optionMenuClass }}"
        [ngClass]="{ 'e11-overflow-menu-options-active': menuActive }"
      >
        <ul>
          <li
            *ngFor="let option of options"
            class="e11-text-sm hover:e11-bg-skin-grey/20 hover:e11-text-skin-dark e11-cursor-pointer"
            (click)="clickOption(option, $event)"
          >
            {{ optionName(option) }}
          </li>
          <ng-content select="[custom-option]"></ng-content>
        </ul>
      </div>
    </div>
  `, isInline: true, styles: [".e11-overflow-menu{position:relative}.e11-overflow-menu .e11-overflow-menu-options{position:absolute;opacity:0;z-index:-1;width:auto;border-radius:4px;overflow-x:clip;height:auto}.e11-overflow-menu .e11-overflow-menu-options.e11-overflow-menu-options-active{opacity:1;z-index:2}.e11-overflow-menu .e11-overflow-menu-options ul{max-width:200px}.e11-overflow-menu .e11-overflow-menu-options ul>li{height:2rem;line-height:2rem;padding:0 1rem;overflow:hidden;overflow-x:hidden;overflow-y:hidden;white-space:nowrap;text-overflow:ellipsis;text-transform:capitalize}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-overflow', standalone: true, imports: [CommonModule], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: E11OverflowComponent,
                            multi: true,
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: `
    <div class="e11-overflow-menu e11-relative {{ containerClass }}" (mouseleave)="mouseOut()">
      <span
        id="{{ idMenuControl }}"
        class="material-icons-outlined e11-cursor-pointer e11-rounded-full e11-p-1 e11-overflow-menu-{{ iconColor }} {{ iconClass }}"
        (click)="toggleMenu($event)"
        [ngClass]="{ active: menuActive }"
      >
        {{ icon }}
      </span>
      <div
        id="{{ idOptionsMenu }}"
        class="e11-overflow-menu-options e11-border e11-border-skin-app-borders e11-bg-white  {{ optionMenuClass }}"
        [ngClass]="{ 'e11-overflow-menu-options-active': menuActive }"
      >
        <ul>
          <li
            *ngFor="let option of options"
            class="e11-text-sm hover:e11-bg-skin-grey/20 hover:e11-text-skin-dark e11-cursor-pointer"
            (click)="clickOption(option, $event)"
          >
            {{ optionName(option) }}
          </li>
          <ng-content select="[custom-option]"></ng-content>
        </ul>
      </div>
    </div>
  `, styles: [".e11-overflow-menu{position:relative}.e11-overflow-menu .e11-overflow-menu-options{position:absolute;opacity:0;z-index:-1;width:auto;border-radius:4px;overflow-x:clip;height:auto}.e11-overflow-menu .e11-overflow-menu-options.e11-overflow-menu-options-active{opacity:1;z-index:2}.e11-overflow-menu .e11-overflow-menu-options ul{max-width:200px}.e11-overflow-menu .e11-overflow-menu-options ul>li{height:2rem;line-height:2rem;padding:0 1rem;overflow:hidden;overflow-x:hidden;overflow-y:hidden;white-space:nowrap;text-overflow:ellipsis;text-transform:capitalize}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { idMenuControl: [{
                type: Input
            }], idOptionsMenu: [{
                type: Input
            }], optionMenuClass: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], showInContent: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconColor: [{
                type: Input
            }], options: [{
                type: Input
            }], menuWidth: [{
                type: Input
            }], menuMinWidth: [{
                type: Input
            }], menuMaxWidth: [{
                type: Input
            }], menuPosition: [{
                type: Input
            }], closeOnSelect: [{
                type: Input
            }], showSelectedIcon: [{
                type: Input
            }], openOnClick: [{
                type: Input
            }], closeOnMouseOut: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], clickOptionEvent: [{
                type: Output
            }], onClickOptionEvent: [{
                type: Output
            }] } });

class E11OverflowModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowModule, imports: [E11OverflowComponent, CommonModule, E11UtilModule], exports: [E11OverflowComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowModule, imports: [E11OverflowComponent, CommonModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11OverflowModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11OverflowComponent, CommonModule, E11UtilModule],
                    exports: [E11OverflowComponent],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11OverflowComponent, E11OverflowModule, UI_OVERFLOW_MENU_DIVIDER };
